import { useState } from "react";
import { Search } from "lucide-react";
import Button from "../components/Button";
import FilterDropdown from "../components/FilterDropdown";
import Pagination from "../components/Pagination";
import SectionHeader from "../components/SectionHeader";
import StatCard, { StatGrid } from "../components/StatCard";
import Tag from "../components/Tag";
import TextLink from "../components/TextLink";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import downloadCsv from "../downloadCsv";
import { apiRequest } from "../api";
import { NO_DATA, formatDateTime, formatNumber, formatPercent, formatUsd } from "../format";
import useApiQuery, { useDebounced } from "../useApiQuery";
import {
  ALLOCATION_ROWS,
  GRANULARITIES,
  LEDGER_DAYS,
  LEDGER_METHODS,
  LEDGER_SOURCES,
  LEDGER_STATUS,
  LEDGER_TYPES,
  METHOD_LABELS,
  SPLIT_PARTS,
  SPLIT_TABS,
} from "./financialsData";
import styles from "./FinancialsPage.module.css";

const PAGE_SIZE = 12;
// Most the CSV export pulls in one go
const EXPORT_LIMIT = 10000;

const COLUMNS = ["Date", "Trans. ID", "Trader", "Account", "Type", "Method", "Gross", "Source", "Status"];

const toOptions = (map) => Object.entries(map).map(([value, label]) => ({ value, label }));

const LEDGER_FILTERS = [
  { key: "type", label: "Type", options: toOptions(LEDGER_TYPES) },
  { key: "method", label: "Method", options: toOptions(LEDGER_METHODS) },
  {
    key: "status",
    label: "Status",
    options: Object.entries(LEDGER_STATUS).map(([value, status]) => ({ value, label: status.label })),
  },
  { key: "days", label: "Time", options: LEDGER_DAYS },
];

const EMPTY_FILTERS = { type: null, method: null, status: null, days: "30" };

// "$38K" for chart axes
function formatCompactUsd(value) {
  return value >= 1000 ? `$${formatNumber(value / 1000)}K` : formatUsd(value);
}

// "+$599" / "−$1,840"
function formatSignedUsd(value) {
  if (value === null || value === undefined) {
    return NO_DATA;
  }
  return `${value < 0 ? "−" : "+"}${formatUsd(Math.abs(value), Number.isInteger(value) ? 0 : 2)}`;
}

function bucketLabel(start, granularity) {
  const date = new Date(start);
  return granularity === "monthly"
    ? date.toLocaleDateString("en-US", { month: "short", year: "2-digit" })
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Small bordered button group (Daily / Weekly / Monthly, Direct / Referred)
function Segmented({ options, value, onChange, label }) {
  return (
    <div className={`${styles.segmented} d-flex`} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`${styles.segment} label-font ${option.value === value ? styles.segmentActive : ""}`}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function overviewStats({ overview }) {
  const perActivation = (amount) => (amount == null ? null : `$${amount} / activation`);
  return [
    {
      label: "Activation fees collected",
      value: formatUsd(overview.activationFees),
      suffix: `${formatNumber(overview.paidActivations)} paid`,
    },
    {
      label: "PB net contribution",
      value: formatUsd(overview.pbNet),
      suffix: perActivation(overview.pbNetPerActivation),
      tone: "brand",
    },
    { label: "PMM allocation", value: formatUsd(overview.pmmAllocation), suffix: perActivation(overview.pmmPerActivation) },
    {
      label: "Liquidity reserve allocation",
      value: formatUsd(overview.liquidityReserve),
      suffix: perActivation(overview.liquidityReservePerActivation),
    },
    // Not computed yet: the API sends null
    {
      label: "Qualification redemptions",
      value: overview.qualificationRedemptions == null ? "Pending" : formatUsd(overview.qualificationRedemptions),
    },
    {
      label: "Referral fees allocation",
      value: formatUsd(overview.referralFees),
      suffix: `${formatNumber(overview.referredActivations)} referred`,
    },
  ];
}

function liquidityStats({ liquidity }) {
  return [
    { label: "Pool balance", value: formatUsd(liquidity.poolBalance), highlight: true },
    { label: "PMM allocation", value: formatUsd(liquidity.pmmAllocation) },
    {
      label: "Liquidity reserve alloc.",
      value: formatUsd(liquidity.liquidityReserve),
      suffix: `${formatNumber(liquidity.pendingCount)} requested`,
      tone: "warning",
    },
    {
      label: "Pending withdrawals",
      value: liquidity.pendingWithdrawals ? `−${formatUsd(liquidity.pendingWithdrawals)}` : formatUsd(0),
      suffix: `${formatNumber(liquidity.pendingCount)} requests`,
    },
    {
      label: "Qualification redemptions",
      value: formatUsd(liquidity.qualificationRedemptions),
      suffix: `${formatUsd(liquidity.qualificationRedemptionsPaid)} paid`,
    },
    {
      label: "Standard withdrawals paid",
      value: formatUsd(liquidity.standardWithdrawalsPaid),
      suffix: `${formatNumber(liquidity.standardWithdrawalsCount)} payouts`,
    },
  ];
}

// The daily / weekly / monthly chart isn't signed off yet; flip this to show it again
const SHOW_REVENUE_CHART = false;

function RevenueCard({ revenue, granularity, onGranularity }) {
  const max = Math.max(...revenue.series.map((bucket) => bucket.total), 0);
  // Every few buckets gets an axis label, plus the latest
  const labelEvery = Math.ceil(revenue.series.length / 5);

  return (
    <div className={`${styles.panel} ${styles.revenuePanel} d-flex flex-column`}>
      <div className={`${styles.panelHead} d-flex align-items-start justify-content-between`}>
        <div>
          <p className={`${styles.miniLabel} label-font`}>Gross activation fees</p>
          <p className={styles.panelValue}>{formatUsd(revenue.gross)}</p>
        </div>
        {SHOW_REVENUE_CHART ? (
          <Segmented label="Revenue period" options={GRANULARITIES} value={granularity} onChange={onGranularity} />
        ) : null}
      </div>

      {!SHOW_REVENUE_CHART ? (
        <div className={`${styles.chart} ${styles.chartPending} d-flex align-items-center justify-content-center`}>
          <p className={`${styles.miniLabel} label-font`}>Daily / weekly / monthly chart · Pending</p>
        </div>
      ) : (
        <div className={`${styles.chart} d-flex`}>
          <div className={`${styles.axis} label-font d-flex flex-column justify-content-between`} aria-hidden="true">
            <span>{formatCompactUsd(max)}</span>
            <span>{formatCompactUsd(max / 2)}</span>
            <span>$0</span>
          </div>
          <div className={styles.plot}>
            <div className={`${styles.bars} d-flex align-items-end`}>
              {revenue.series.map((bucket, i) => (
                <span
                  key={bucket.start}
                  className={`${styles.bar} ${i === revenue.series.length - 1 ? styles.barCurrent : ""}`}
                  style={{ height: max ? `${(bucket.total / max) * 100}%` : 0 }}
                  title={`${bucketLabel(bucket.start, granularity)}: ${formatUsd(bucket.total)} · ${bucket.count} paid`}
                />
              ))}
            </div>
            <div className={`${styles.ticks} label-font d-flex`} aria-hidden="true">
              {revenue.series.map((bucket, i) => (
                <span key={bucket.start}>
                  {i % labelEvery === 0 || i === revenue.series.length - 1 ? bucketLabel(bucket.start, granularity) : ""}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <dl className={`${styles.revenueStats} d-flex`}>
        {[
          { label: "Paid activations", value: formatNumber(revenue.paidActivations) },
          {
            label: "Direct",
            value: formatNumber(revenue.direct),
            share: revenue.paidActivations ? (revenue.direct / revenue.paidActivations) * 100 : null,
          },
          {
            label: "Referred",
            value: formatNumber(revenue.referred),
            share: revenue.paidActivations ? (revenue.referred / revenue.paidActivations) * 100 : null,
          },
          // Paid after qualifying on a free account; a subset of direct + referred
          { label: "Free-to-paid", value: formatNumber(revenue.freeToPaid) },
          { label: "Avg / day", value: formatNumber(revenue.avgPerDay, 1) },
        ].map((stat) => (
          <div key={stat.label} className={styles.revenueStat}>
            <dt className={`${styles.miniLabel} label-font`}>{stat.label}</dt>
            <dd>
              {stat.value}
              {stat.share !== undefined && stat.share !== null ? (
                <span className={styles.share}> {formatPercent(stat.share, 0)}</span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function PaymentMixCard({ mix }) {
  return (
    <div className={`${styles.panel} d-flex flex-column`}>
      <div className={styles.mixHead}>
        <div className={`${styles.mixBar} d-flex`} aria-hidden="true">
          {mix.rows.map((row) =>
            row.share ? (
              <span key={row.method} className={styles[`mix${METHOD_LABELS[row.method]}`]} style={{ width: `${row.share}%` }} />
            ) : null
          )}
          {mix.total.volume ? null : <span className={styles.mixEmpty} />}
        </div>
        <ul className={`${styles.legend} label-font d-flex flex-wrap`}>
          {mix.rows.map((row) => (
            <li key={row.method} className="d-flex align-items-center">
              <span className={`${styles.legendSwatch} ${styles[`mix${METHOD_LABELS[row.method]}`]}`} aria-hidden="true" />
              {METHOD_LABELS[row.method]} {formatPercent(row.share, 0)}
            </li>
          ))}
        </ul>
      </div>
      <div className={tableStyles.wrap}>
        <table className={`${tableStyles.table} ${styles.mixTable}`}>
          <thead>
            <tr>
              {["Method", "Payments", "Volume", "Success", "Failed", "CB"].map((heading) => (
                <th key={heading} scope="col">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...mix.rows, mix.total].map((row) => (
              <tr key={row.method} className={row.method === "total" ? styles.totalRow : ""}>
                <td>{METHOD_LABELS[row.method]}</td>
                <td>{formatNumber(row.payments)}</td>
                <td>{formatUsd(row.volume)}</td>
                <td className={styles.toneSuccess}>{formatNumber(row.success)}</td>
                <td>{formatNumber(row.failed)}</td>
                <td>{formatNumber(row.chargebacks)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EconomicsCard({ price, split }) {
  const parts = SPLIT_PARTS.filter((part) => split[part.key] > 0);

  return (
    <div className={`${styles.panel} ${styles.economics} d-flex flex-column`}>
      <div className={`${styles.stack} d-flex`}>
        {parts.map((part) => (
          <span
            key={part.key}
            className={`${styles.stackPart} ${styles[part.swatch]} label-font`}
            style={{ flexGrow: split[part.key] }}
          >
            ${split[part.key]}
          </span>
        ))}
      </div>
      <ul className={styles.splitList}>
        {parts.map((part) => (
          <li key={part.key} className={`${styles.splitRow} ${part.strong ? styles.splitStrong : ""} d-flex align-items-center`}>
            <span className={`${styles.swatch} ${styles[part.swatch]}`} aria-hidden="true" />
            <span className="flex-grow-1">{part.label}</span>
            <span className="label-font">${split[part.key]}</span>
          </li>
        ))}
      </ul>
      <p className={`${styles.equation} label-font`}>
        ${price} = {parts.map((part) => `$${split[part.key]}`).join(" + ")}
      </p>
    </div>
  );
}

function AllocationCard({ allocation }) {
  const { gross } = allocation;
  const pctOf = (value) => (gross ? (value / gross) * 100 : 0);
  let remaining = gross;
  const rows = ALLOCATION_ROWS.map((row) => {
    const amount = allocation[row.key];
    const left = pctOf(remaining - amount);
    remaining -= amount;
    return { ...row, amount, left, width: pctOf(amount) };
  });

  return (
    <div className={`${styles.panel} ${styles.allocation} d-flex flex-column`}>
      <div className={`${styles.allocationRow} ${styles.allocationStrong} d-flex align-items-center`}>
        <span className={styles.allocationLabel}>Gross activation fees</span>
        <span className={styles.track}>
          <span className={`${styles.fill} ${styles.swatchGross}`} style={{ left: 0, width: gross ? "100%" : 0 }} />
        </span>
        <span className={`${styles.allocationValue} label-font`}>{formatUsd(gross)}</span>
      </div>
      {rows.map((row) => (
        <div key={row.key} className={`${styles.allocationRow} d-flex align-items-center`}>
          <span className={styles.allocationLabel}>{row.label}</span>
          <span className={styles.track}>
            <span className={`${styles.fill} ${styles[row.swatch]}`} style={{ left: `${row.left}%`, width: `${row.width}%` }} />
          </span>
          <span className={`${styles.allocationValue} label-font`}>
            {row.amount ? `−${formatUsd(row.amount)}` : formatUsd(0)}
          </span>
        </div>
      ))}
      <div className={`${styles.allocationRow} ${styles.allocationStrong} ${styles.allocationTotal} d-flex align-items-center`}>
        <span className={styles.allocationLabel}>PB net contribution</span>
        <span className={styles.track}>
          <span
            className={`${styles.fill} ${styles.swatchNet}`}
            style={{ left: 0, width: `${Math.max(0, pctOf(allocation.pbNet))}%` }}
          />
        </span>
        <span className={`${styles.allocationValue} label-font`}>{formatUsd(allocation.pbNet)}</span>
      </div>
    </div>
  );
}

function FundingSourceCard({ fundingSource }) {
  const coverage = fundingSource.pmmCoverage;

  return (
    <div className={`${styles.panel} ${styles.funding} d-flex flex-column`}>
      <div className="d-flex justify-content-between flex-wrap gap-3">
        <div>
          <p className={`${styles.miniLabel} label-font`}>PMM coverage ratio</p>
          <p className={`${styles.panelValue} ${styles.toneSuccess}`}>{formatPercent(coverage)}</p>
        </div>
        <div className="text-end">
          <p className={`${styles.miniLabel} label-font`}>Total withdrawals paid</p>
          <p className={styles.fundingTotal}>{formatUsd(fundingSource.totalWithdrawalsPaid)}</p>
        </div>
      </div>
      <div className={`${styles.coverageBar} d-flex`} aria-hidden="true">
        <span className={styles.mixCard} style={{ width: `${coverage ?? 0}%` }} />
        <span className={`${styles.coverageRest} flex-grow-1`} />
      </div>
      <div className={`${styles.fundingSplit} d-flex`}>
        {[
          { label: "PMM-offset", value: fundingSource.pmmOffset, swatch: styles.mixCard },
          { label: "Pool-funded", value: fundingSource.poolFunded, swatch: styles.coverageRest },
        ].map((item) => (
          <div key={item.label} className="flex-grow-1">
            <p className={`${styles.miniLabel} label-font d-flex align-items-center`}>
              <span className={`${styles.legendSwatch} ${item.swatch}`} aria-hidden="true" />
              {item.label}
            </p>
            <p className={styles.fundingValue}>{formatUsd(item.value)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ledgerCells(row) {
  return [
    formatDateTime(row.date),
    row.reference,
    row.trader ?? NO_DATA,
    row.account ?? NO_DATA,
    LEDGER_TYPES[row.type],
    LEDGER_METHODS[row.method],
    formatSignedUsd(row.gross),
    LEDGER_SOURCES[row.source],
    LEDGER_STATUS[row.status].label,
  ];
}

// Ledger search, filters and page, kept by the page so the header's export follows them
function useLedger() {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const debouncedQuery = useDebounced(query);
  const apiQuery = {
    type: filters.type,
    method: filters.method,
    status: filters.status,
    days: filters.days,
    q: debouncedQuery.trim(),
  };
  const result = useApiQuery("/api/admin/financials/ledger", { ...apiQuery, page, pageSize: PAGE_SIZE });

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };
  const updateQuery = (value) => {
    setQuery(value);
    setPage(1);
  };

  return { ...result, query, updateQuery, filters, updateFilter, page, setPage, apiQuery };
}

async function exportLedger(apiQuery) {
  const { transactions } = await apiRequest("/api/admin/financials/ledger", {
    query: { ...apiQuery, page: 1, pageSize: EXPORT_LIMIT },
  });
  downloadCsv("transactions.csv", COLUMNS, transactions.map(ledgerCells));
}

function TransactionLedger({ ledger }) {
  const { data, status, reload, query, updateQuery, filters, updateFilter, page, setPage } = ledger;
  const visible = status === "error" ? [] : data?.transactions ?? [];
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <section>
      <SectionHeader title="Transaction ledger" />
      <div className={`${controlStyles.toolbar} d-flex align-items-center justify-content-between`}>
        <div className={`${styles.ledgerFilters} d-flex flex-wrap`}>
          {LEDGER_FILTERS.map((filter) => (
            <FilterDropdown
              key={filter.key}
              label={filter.label}
              options={filter.options}
              value={filters[filter.key]}
              onChange={(value) => updateFilter(filter.key, value)}
            />
          ))}
        </div>
        <label className={`${controlStyles.search} d-flex align-items-center`}>
          <Search size={20} strokeWidth={1.75} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search"
            aria-label="Search transactions"
            value={query}
            onChange={(event) => updateQuery(event.target.value)}
          />
        </label>
      </div>

      <div className={`${tableStyles.wrap} ${styles.ledgerTable}`}>
        <table className={tableStyles.table}>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const cells = ledgerCells(row);
              const rowStatus = LEDGER_STATUS[row.status];
              return (
                <tr key={`${row.type}-${row.id}`}>
                  {cells.slice(0, -1).map((cell, i) => (
                    <td key={COLUMNS[i]}>{cell}</td>
                  ))}
                  <td>
                    <Tag tone={rowStatus.tone}>{rowStatus.label}</Tag>
                  </td>
                </tr>
              );
            })}
            {!data && status === "loading" ? (
              <tr>
                <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                  Loading transactions…
                </td>
              </tr>
            ) : status === "error" ? (
              <tr>
                <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                  We couldn't load transactions.{" "}
                  <TextLink arrow={false} onClick={reload}>
                    Try again
                  </TextLink>
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                  No transactions match these filters.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination page={Math.min(page, pageCount)} pageCount={pageCount} onChange={setPage} />
    </section>
  );
}

export default function FinancialsPage() {
  const [granularity, setGranularity] = useState("daily");
  const [splitTab, setSplitTab] = useState("direct");
  const { data, status, reload } = useApiQuery("/api/admin/financials", { granularity });
  const ledger = useLedger();
  const [exporting, setExporting] = useState(false);

  // Exports the transaction ledger with its current filters
  const handleExport = async () => {
    setExporting(true);
    try {
      await exportLedger(ledger.apiQuery);
    } catch (err) {
      console.error("Transaction export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const header = (
    <header className={`${styles.pageHeader} d-flex align-items-start justify-content-between`}>
      <div className={styles.heading}>
        <h1 className={styles.title}>Financials</h1>
        <p className={styles.subtitle}>
          Track activation revenue, payment behaviour, fund allocation, withdrawals and platform liquidity.
        </p>
      </div>
      <Button variant="outline" onClick={handleExport} disabled={exporting || !ledger.data?.total}>
        {exporting ? "Exporting…" : "Export financials"}
      </Button>
    </header>
  );

  if (!data) {
    return (
      <section className={styles.page}>
        {header}
        <p className={styles.subtitle}>
          {status === "error" ? (
            <>
              We couldn't load financials.{" "}
              <TextLink arrow={false} onClick={reload}>
                Try again
              </TextLink>
            </>
          ) : (
            "Loading…"
          )}
        </p>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      {header}

      <section>
        <SectionHeader title="Overview" />
        <StatGrid>
          {overviewStats(data).map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </StatGrid>
      </section>

      <div className={styles.revenueRow}>
        <section className="d-flex flex-column">
          <SectionHeader title="Activation revenue" />
          <RevenueCard revenue={data.revenue} granularity={granularity} onGranularity={setGranularity} />
        </section>
        <section className="d-flex flex-column">
          <SectionHeader title="Payment method mix" />
          <PaymentMixCard mix={data.paymentMix} />
        </section>
      </div>

      <div className={styles.pairRow}>
        <section className="d-flex flex-column">
          <SectionHeader
            title={`$${data.price} activation economics`}
            aside={<Segmented label="Trader source" options={SPLIT_TABS} value={splitTab} onChange={setSplitTab} />}
          />
          <EconomicsCard price={data.price} split={data.split[splitTab]} />
        </section>
        <section className="d-flex flex-column">
          <SectionHeader title="Revenue allocation · period totals" />
          <AllocationCard allocation={data.allocation} />
        </section>
      </div>

      <section>
        <SectionHeader title="Withdrawal liquidity" />
        <StatGrid>
          {liquidityStats(data).map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </StatGrid>
      </section>

      <div className={styles.pairRow}>
        <section className="d-flex flex-column">
          <SectionHeader title="Withdrawal funding source" />
          <FundingSourceCard fundingSource={data.fundingSource} />
        </section>
        <section className="d-flex flex-column">
          <SectionHeader title="PMM financial performance" />
          <div className={styles.pmmGrid}>
            <StatCard label="Capital allocated" value={formatUsd(data.pmm.capitalAllocated)} />
            <StatCard label="Total realized PNL" value={formatUsd(data.pmm.realizedPnl)} tone="brand" />
            <StatCard label="Transferred to pool" value={formatUsd(data.pmm.transferredToPool)} />
            <StatCard label="Return on allocation" value={formatPercent(data.pmm.returnOnAllocation)} />
          </div>
        </section>
      </div>

      <TransactionLedger ledger={ledger} />
    </section>
  );
}
